<template>
    <div class="admin-connections-page">
        <div class="page-header">
            <h2>Connections</h2>

            <div class="add-place-box form-wrapper-one">
                <input
                    v-model="searchQuery"
                    type="text"
                    placeholder="Search by cid, uid, requester, name, email or status..."
                    class="form-control"
                />
            </div>
        </div>

        <div v-if="loading" class="state-box">
            Зареждане на connections...
        </div>

        <div v-else-if="errorMessage" class="state-box error">
            {{ errorMessage }}
        </div>

        <div v-else class="places-card">
            <div class="places-card-header">
                <h4>Connections</h4>
                <span class="places-count">{{ filteredConnections.length }} items</span>
            </div>

            <div v-if="!filteredConnections.length" class="state-box">
                Няма намерени connections.
            </div>

            <div v-else class="table-wrap">
                <table class="places-table">
                    <thead>
                    <tr>
                        <th>CID</th>
                        <th>Status</th>
                        <th>Requested By</th>
                        <th>User A</th>
                        <th>User B</th>
                        <th>Created</th>
                        <th>Claimed</th>
                        <th>Accepted</th>
                        <th>Updated</th>
                    </tr>
                    </thead>

                    <tbody>
                    <tr v-for="connection in filteredConnections" :key="connection.id">
                        <td>{{ connection.cid || connection.id || '-' }}</td>

                        <td>
                            <span :class="statusBadgeClass(connection.status)">
                                {{ connection.status || '-' }}
                            </span>
                        </td>

                        <!-- REQUESTED BY -->
                        <td>
                            <div class="user-cell">
                                <div class="photo-cell">
                                    <img
                                        v-if="getUser(connection.requestedBy)?.avatarUrl"
                                        :src="getUser(connection.requestedBy).avatarUrl"
                                        alt="avatar"
                                        class="place-photo"
                                        @error="onImageError"
                                    />
                                    <span v-else>-</span>
                                </div>

                                <div class="user-meta">
                                    <div class="user-name">
                                        {{ getUserDisplayName(connection.requestedBy) }}
                                    </div>

                                    <div class="user-email">
                                        {{ getUser(connection.requestedBy)?.email || '-' }}
                                    </div>

                                    <div class="user-uid">
                                        {{ connection.requestedBy }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <!-- USER A -->
                        <td>
                            <div class="user-cell">
                                <div class="photo-cell">
                                    <img
                                        v-if="getUser(connection.uidA)?.avatarUrl"
                                        :src="getUser(connection.uidA).avatarUrl"
                                        alt="avatar"
                                        class="place-photo"
                                        @error="onImageError"
                                    />
                                    <span v-else>-</span>
                                </div>

                                <div class="user-meta">
                                    <div class="user-name">
                                        {{ getUserDisplayName(connection.uidA) }}
                                    </div>

                                    <div class="user-email">
                                        {{ getUser(connection.uidA)?.email || '-' }}
                                    </div>

                                    <div class="user-uid">
                                        {{ connection.uidA }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <!-- USER B -->
                        <td>
                            <div class="user-cell">
                                <div class="photo-cell">
                                    <img
                                        v-if="getUser(connection.uidB)?.avatarUrl"
                                        :src="getUser(connection.uidB).avatarUrl"
                                        alt="avatar"
                                        class="place-photo"
                                        @error="onImageError"
                                    />
                                    <span v-else>-</span>
                                </div>

                                <div class="user-meta">
                                    <div class="user-name">
                                        {{ getUserDisplayName(connection.uidB) }}
                                    </div>

                                    <div class="user-email">
                                        {{ getUser(connection.uidB)?.email || '-' }}
                                    </div>

                                    <div class="user-uid">
                                        {{ connection.uidB }}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <td>{{ formatAnyDate(connection.createdAt) }}</td>
                        <td>{{ formatAnyDate(connection.claimedAt) }}</td>
                        <td>{{ formatAnyDate(connection.acceptedAt) }}</td>
                        <td>{{ formatAnyDate(connection.updatedAt) }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import { collection, getDocs } from "firebase/firestore";
import { sentrysDb } from "@/firebase";

export default {
    name: "AdminConnections",

    data() {
        return {
            loading: false,
            errorMessage: "",
            connections: [],
            usersByUid: {},
            searchQuery: "",
        };
    },

    computed: {
        filteredConnections() {
            const q = (this.searchQuery || "").trim().toLowerCase();
            if (!q) return this.connections;

            return this.connections.filter((item) => {
                const requestedUser = this.getUser(item.requestedBy);
                const userA = this.getUser(item.uidA);
                const userB = this.getUser(item.uidB);

                const haystack = [
                    item.cid,
                    item.status,
                    item.requestedBy,
                    item.uidA,
                    item.uidB,

                    requestedUser?.firstName,
                    requestedUser?.email,

                    userA?.firstName,
                    userA?.email,

                    userB?.firstName,
                    userB?.email,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();

                return haystack.includes(q);
            });
        }
    },

    async mounted() {
        await this.fetchConnectionsAndUsers();
    },

    methods: {
        async fetchConnectionsAndUsers() {
            this.loading = true;
            this.errorMessage = "";

            try {
                const [connectionsSnapshot, usersSnapshot] = await Promise.all([
                    getDocs(collection(sentrysDb, "connections")),
                    getDocs(collection(sentrysDb, "users")),
                ]);

                this.connections = connectionsSnapshot.docs
                    .map((docItem) => ({
                        id: docItem.id,
                        ...docItem.data(),
                    }))
                    .sort((a, b) => {
                        const aTime = this.toMillis(a.updatedAt || a.createdAt);
                        const bTime = this.toMillis(b.updatedAt || b.createdAt);
                        return bTime - aTime;
                    });

                const usersMap = {};
                usersSnapshot.docs.forEach((docItem) => {
                    usersMap[docItem.id] = {
                        id: docItem.id,
                        ...docItem.data(),
                    };
                });

                this.usersByUid = usersMap;
            } catch (error) {
                console.error("Fetch connections/users error:", error);
                this.errorMessage = "Грешка при зареждане на connections.";
            } finally {
                this.loading = false;
            }
        },

        getUser(uid) {
            if (!uid) return null;
            return this.usersByUid[uid] || null;
        },

        getUserDisplayName(uid) {
            const user = this.getUser(uid);
            if (!user) return this.shortText(uid);
            return user.firstName || this.shortText(uid);
        },

        shortText(value, max = 24) {
            if (!value) return "-";
            return value.length > max ? value.slice(0, max) + "..." : value;
        },

        toMillis(value) {
            if (!value) return 0;

            if (typeof value === "number") return value;

            if (value.toDate && typeof value.toDate === "function") {
                return value.toDate().getTime();
            }

            const parsed = new Date(value).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
        },

        formatAnyDate(value) {
            const millis = this.toMillis(value);
            if (!millis) return "-";

            return new Intl.DateTimeFormat("bg-BG", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            }).format(new Date(millis));
        },

        statusBadgeClass(status) {
            const normalized = (status || "").toLowerCase();

            if (normalized === "accepted") return "badge yes";
            if (normalized === "pending") return "badge no";

            return "badge";
        },

        onImageError(e) {
            e.target.src = "https://via.placeholder.com/60x60?text=User";
        },
    },
};
</script>

<style lang="scss">
@import './scss/styles.scss';
</style>